// src/Muestras/Drawer/useMuestraData.js
import { useEffect, useState } from "react";
import {
  getMuestraById,
  addMuestra,
  updateMuestra,
  getEscandallosByMuestraId,
  addEscandallo,
  updateEscandallo,
  deleteEscandallo,
  getMovimientosMuestras,
  addMovimiento,
  updateMovimiento,
  getEscandalloSubgruposMuestras,
} from "../../../api/Muestras/muestraApi";
import {
  formatMuestraDateForAPI,
  parseMuestraDate,
} from "../../../utils/dateUtils";
import { blocksNewShipmentUntilReception } from "./muestraEstadoHelpers";

export default function useMuestraData({
  open,
  isEditMode,
  newMuestra,
  tiposMuestra,
  selectedProduct,
  escandallosIniciales,
}) {
  const [loading, setLoading] = useState(true);
  const [muestra, setMuestra] = useState(newMuestra || {});

  const [escandallos, setEscandallos] = useState(escandallosIniciales || []);
  const [originalEscandallos, setOriginalEscandallos] = useState(
    escandallosIniciales || [],
  );

  const [movimientos, setMovimientos] = useState([]);

  const resolvedProductId =
    selectedProduct?.id ??
    selectedProduct?.ProductId ??
    selectedProduct?.productId ??
    selectedProduct?.ID_PRODUCTO ??
    newMuestra?.ID_PRODUCTO ??
    null;

  // ----------------------------------------------------------------------
  // INIT
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!open) return;

    const load = async () => {
      setLoading(true);

      console.log("🔎 INIT MUESTRA:", {
        isEditMode,
        newMuestra,
      });

      try {
        if (isEditMode && newMuestra?.ID_MUESTRA) {
          const data = await getMuestraById(newMuestra.ID_MUESTRA);
          const esc = await getEscandallosByMuestraId(newMuestra.ID_MUESTRA);
          let mov = [];
          try {
            mov = await getMovimientosMuestras(newMuestra.ID_MUESTRA);
          } catch (movErr) {
            console.error("Error cargando movimientos:", movErr);
            mov = [];
          }

          setMuestra({
            ...newMuestra,
            ...data,
            FECHA_PETICION: parseMuestraDate(data.FECHA_PETICION),
            FECHA_PREVISTA: parseMuestraDate(data.FECHA_PREVISTA),
            FECHA_ENVIO: parseMuestraDate(data.FECHA_ENVIO),
            FECHA_RECEPCION: parseMuestraDate(data.FECHA_RECEPCION),
            FECHA_GET_SUPPLIER: parseMuestraDate(data.FECHA_GET_SUPPLIER),
            FECHA_PREVISTA_FABRICA: parseMuestraDate(
              data.FECHA_PREVISTA_FABRICA,
            ),
          });

          const escCompleto = await Promise.all(
            esc.map(async (e) => {
              let subgrupos = [];

              if (e.ESCANDALLO_GRUPOS) {
                const res = await getEscandalloSubgruposMuestras(
                  e.ESCANDALLO_GRUPOS,
                );
                subgrupos = res.map((s) => ({
                  ID_SUBGRUPO: s.ID_SUBGRUPO,
                  SUBGRUPO: s.SUBGRUPO,
                }));
              }

              return {
                ...e,
                ID_ESCANDALLO: e.ID_ESCANDALLO,
                PROVEEDOR_ESCANDALLO: e.Proveedor,
                UN: e.Un,
                COSTE: e.Coste,
                SUBGRUPOS_LIST: subgrupos,
              };
            }),
          );

          setEscandallos(escCompleto);
          setOriginalEscandallos(escCompleto);
          setMovimientos(mov || []);
        } else {
          const tipo = tiposMuestra?.find(
            (t) => t.ID_TIPO === newMuestra.ID_TIPO_MUESTRA,
          );
          const abbr = tipo?.ABREVIATURA || "";

          setMuestra({
            ...newMuestra,
            MUESTRA: `SAMP-${Math.random()
              .toString(36)
              .substring(2, 7)
              .toUpperCase()}-${abbr}`,
            ID_PRODUCTO: resolvedProductId,
            FECHA_GET_SUPPLIER: null,
          });

          setEscandallos([{ PROVEEDOR_ESCANDALLO: "", UN: "", COSTE: "" }]);
        }
      } catch (err) {
        console.error("Error inicializando drawer de muestra:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, isEditMode, resolvedProductId, newMuestra?.ID_MUESTRA]);

  // ----------------------------------------------------------------------
  // SAVE
  // ----------------------------------------------------------------------
  // ----------------------------------------------------------------------
  const saveMuestra = async (overrideData = null) => {
    let saved;

    const dataSource = overrideData || muestra;

    // -----------------------------
    // PREPARAR PAYLOAD LIMPIO
    // -----------------------------
    const payload = {
      // ✅ CAMPO OBLIGATORIO
      MUESTRA: dataSource.MUESTRA,

      ID_TIPO_MUESTRA:
        dataSource.ID_TIPO_MUESTRA && dataSource.ID_TIPO_MUESTRA !== ""
          ? Number(dataSource.ID_TIPO_MUESTRA)
          : null,

      ID_ESTADO: dataSource.ID_ESTADO,
      ID_TALLA_BASE: dataSource.ID_TALLA_BASE,
      ID_COLOR_BASE: dataSource.ID_COLOR_BASE,
      PROVEEDOR:
        dataSource.PROVEEDOR == null || dataSource.PROVEEDOR === ""
          ? null
          : String(dataSource.PROVEEDOR),
      ID_Marca: dataSource.ID_Marca,
      ID_RESPONSABLE_SUPPLIER:
        dataSource.ID_RESPONSABLE_SUPPLIER === "" ||
        dataSource.ID_RESPONSABLE_SUPPLIER == null
          ? null
          : Number(dataSource.ID_RESPONSABLE_SUPPLIER),

      // Producto siempre resuelto
      ID_PRODUCTO: resolvedProductId || dataSource.ID_PRODUCTO || null,

      // Nunca enviar null
      NombreCliente:
        dataSource.NombreCliente ||
        dataSource?.marca?.MARCA ||
        dataSource?.NOMBRE_CLIENTE ||
        "Desconocido",

      FECHA_PETICION: formatMuestraDateForAPI(dataSource.FECHA_PETICION),
      FECHA_PREVISTA: formatMuestraDateForAPI(dataSource.FECHA_PREVISTA),
      FECHA_GET_SUPPLIER: formatMuestraDateForAPI(
        dataSource.FECHA_GET_SUPPLIER,
      ),
      FECHA_ENVIO: formatMuestraDateForAPI(dataSource.FECHA_ENVIO),
      FECHA_RECEPCION: formatMuestraDateForAPI(dataSource.FECHA_RECEPCION),
      FECHA_PREVISTA_FABRICA: formatMuestraDateForAPI(
        dataSource.FECHA_PREVISTA_FABRICA,
      ),
      PRIORIDAD: dataSource.PRIORIDAD || null,
      DEVELOPMENT: dataSource.DEVELOPMENT || null,
      CALIDAD_NOMBRE: dataSource.CALIDAD_NOMBRE || null,
      CALIDAD_REFERENCIA: dataSource.CALIDAD_REFERENCIA || null,
      CALIDAD_MATERIAL: dataSource.CALIDAD_MATERIAL || null,
      CALIDAD_ESTADO: dataSource.CALIDAD_ESTADO || null,
      PRINT_NOMBRE: dataSource.PRINT_NOMBRE || null,
      PRINT_ESTADO: dataSource.PRINT_ESTADO || null,
      NOTAS: dataSource.NOTAS,
    };

    if (!payload.MUESTRA) {
      throw new Error("MUESTRA missing when saving muestra");
    }

    if (!payload.ID_PRODUCTO) {
      console.error("❌ ID_PRODUCTO missing", {
        selectedProduct,
        newMuestra,
        payload,
      });
      return null;
    }

    // -----------------------------
    // UPDATE EXISTENTE
    // -----------------------------
    if (dataSource.ID_MUESTRA) {
      saved = await updateMuestra(dataSource.ID_MUESTRA, payload);
    }

    // -----------------------------
    // CREAR NUEVA
    // -----------------------------
    else {
      console.log("🧪 CREATE MUESTRA", payload);

      const createWithUniqueName = async (basePayload, attempt = 0) => {
        try {
          return await addMuestra(basePayload);
        } catch (err) {
          const status = err?.response?.status;
          const code = err?.response?.data?.code;
          if (attempt < 2 && (status === 409 || code === "DUPLICATE_MUESTRA")) {
            const tipo = tiposMuestra?.find(
              (t) => t.ID_TIPO === Number(basePayload.ID_TIPO_MUESTRA),
            );
            const abbr = tipo?.ABREVIATURA || "";
            const random = Math.random()
              .toString(36)
              .substring(2, 7)
              .toUpperCase();
            const nextName = `SAMP-${random}${abbr ? `-${abbr}` : ""}`;
            return createWithUniqueName(
              { ...basePayload, MUESTRA: nextName },
              attempt + 1,
            );
          }
          throw err;
        }
      };

      saved = await createWithUniqueName(payload);

      // Marcar ya como existente por si falla el GET posterior (evita re-POST duplicado)
      setMuestra((prev) => ({
        ...prev,
        ...payload,
        ...saved,
        ID_MUESTRA: saved.ID_MUESTRA,
        MUESTRA: saved.MUESTRA || payload.MUESTRA,
      }));

      try {
        const fetched = await getMuestraById(saved.ID_MUESTRA);
        setMuestra(fetched);
      } catch (fetchErr) {
        console.error(
          "Muestra creada pero no se pudo recargar; se sigue con la respuesta del POST",
          fetchErr,
        );
      }
    }

    // -------------------------------------------------------------------
    // ESCANDALLOS — CREAR / ACTUALIZAR (en paralelo)
    // -------------------------------------------------------------------
    await Promise.all(
      escandallos.map(async (esc) => {
        if (esc.ID_ESCANDALLO || esc.Escandallo_Id) {
          await updateEscandallo(esc.ID_ESCANDALLO || esc.Escandallo_Id, {
            Proveedor: esc.PROVEEDOR_ESCANDALLO,
            Un: esc.UN,
            Coste: esc.COSTE,
            ESCANDALLO_GRUPOS: esc.ESCANDALLO_GRUPOS,
            ESCANDALLO_SUBGRUPO: esc.ESCANDALLO_SUBGRUPO,
          });
        } else if (esc.ESCANDALLO_GRUPOS && esc.ESCANDALLO_SUBGRUPO) {
          await addEscandallo({
            ID_MUESTRA: saved.ID_MUESTRA,
            Proveedor: esc.PROVEEDOR_ESCANDALLO,
            Un: esc.UN,
            Coste: esc.COSTE,
            ESCANDALLO_GRUPOS: esc.ESCANDALLO_GRUPOS,
            ESCANDALLO_SUBGRUPO: esc.ESCANDALLO_SUBGRUPO,
          });
        }
      }),
    );

    // -------------------------------------------------------------------
    // MOVIMIENTOS — Update o Create (en paralelo)
    // -------------------------------------------------------------------
    await Promise.all(
      movimientos.map(async (mov) => {
        const tipo = String(mov.TIPO_DESTINO || "").toUpperCase();
        const hasNewDestino =
          (tipo === "CLIENTE" && mov.ID_CLIENTE) ||
          (tipo === "PROVEEDOR" && mov.CODPROV) ||
          tipo === "INTERNO";
        const hasLegacyDestino = mov.ID_Marca != null && mov.ID_Marca !== "";

        if (mov.ID_MOVIMIENTO) {
          if (!mov.FECHA_ENVIO || (!hasNewDestino && !hasLegacyDestino)) return;
          await updateMovimiento(mov.ID_MOVIMIENTO, mov);
        } else if (mov.FECHA_ENVIO && (hasNewDestino || hasLegacyDestino)) {
          if (
            blocksNewShipmentUntilReception(
              saved.ID_ESTADO ?? muestra.ID_ESTADO,
              [],
              saved.ESTADO_NOMBRE || muestra.ESTADO_NOMBRE || null,
            )
          ) {
            return;
          }
          await addMovimiento({
            ID_MUESTRA: saved.ID_MUESTRA,
            ID_Marca: hasNewDestino ? null : mov.ID_Marca,
            FECHA_ENVIO: mov.FECHA_ENVIO,
            NUM_COURIER: mov.NUM_COURIER || null,
            TIPO_DESTINO: mov.TIPO_DESTINO || null,
            ID_CLIENTE: mov.ID_CLIENTE ?? null,
            CODPROV: mov.CODPROV ?? null,
            ID_CONTACTO: mov.ID_CONTACTO ?? null,
            PERSONA_ENVIO: mov.PERSONA_ENVIO || null,
          });
        }
      }),
    );

    return saved;
  };

  // ----------------------------------------------------------------------
  return {
    loading,
    muestra,
    setMuestra,
    escandallos,
    setEscandallos,
    movimientos,
    setMovimientos,
    saveMuestra,
  };
}
